import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useSocket } from '../contexts/SocketContext';

export default function Presentation() {
  const { gameId } = useParams();
  const { 
    game, 
    activeQuestion, 
    timeRemaining, 
    questionResults,
    finalResults,
    isConnected,
    joinAsAdmin 
  } = useSocket();

  useEffect(() => {
    if (gameId && isConnected) {
      joinAsAdmin(gameId);
    }
  }, [gameId, isConnected]);

  if (!game) {
    return (
      <div className="presentation">
        <div className="presentation-content">
          <h1>Laden...</h1>
        </div>
      </div>
    );
  }

  const joinUrl = `${window.location.origin}/join/${game.id}`;

  return (
    <div className="presentation">
      {/* Header */}
      <div className="presentation-header">
        <h2 style={{ color: 'var(--color-primary)' }}>WIE IS DE MOL?</h2>
        <div className="flex gap-2">
          <span className="text-muted">Code: {game.id}</span>
          {game.status === 'playing' && (
            <span>Vraag {game.currentQuestionIndex + 1}/{game.questions.length}</span>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="presentation-content">
        {/* Lobby */}
        {game.status === 'lobby' && (
          <div className="text-center animate-fadeIn">
            <h1 style={{ fontSize: '5rem', color: 'var(--color-primary)', marginBottom: '20px' }}>
              WIE IS DE MOL?
            </h1>
            <p style={{ fontSize: '2rem', marginBottom: '40px' }}>Scan de QR-code om mee te doen!</p>
            
            <div className="qr-container" style={{ marginBottom: '40px' }}>
              <QRCodeSVG value={joinUrl} size={300} />
            </div>
            
            <p style={{ fontSize: '1.5rem', color: 'var(--color-text-muted)' }}>
              Of ga naar: <span style={{ color: 'var(--color-accent)' }}>{joinUrl}</span>
            </p>
            
            <div className="mt-3">
              <h3>Teams ({game.teams.length})</h3>
              <div className="flex gap-2 mt-2" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                {game.teams.map(team => (
                  <div 
                    key={team.id} 
                    className="card animate-fadeIn"
                    style={{ padding: '12px 24px' }}
                  >
                    <span style={{ fontWeight: 600 }}>{team.name}</span>
                    <span className="text-muted" style={{ marginLeft: '8px' }}>
                      ({team.players.length} spelers)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Active Question */}
        {game.status === 'playing' && activeQuestion && (
          <div className="text-center animate-fadeIn" style={{ width: '100%', maxWidth: '1000px' }}>
            {/* Timer */}
            <div className={`timer ${timeRemaining <= 5 ? 'warning' : ''}`} style={{ marginBottom: '20px' }}>
              {timeRemaining}
            </div>
            
            {/* Hint */}
            {activeQuestion.displayedHint && (
              <div 
                className={`hint-box ${activeQuestion.sabotage?.type === 'wrong_hint' ? 'sabotaged' : ''}`}
              >
                💡 Hint: {activeQuestion.displayedHint}
              </div>
            )}
            
            {/* Question */}
            <h2 className="question-text">{activeQuestion.question.text}</h2>
            
            {/* Options */}
            <div className="options-grid">
              {activeQuestion.question.options.map((option, index) => (
                <div 
                  key={index}
                  className={`option-btn ${activeQuestion.hiddenOptions.includes(index) ? 'hidden' : ''}`}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Question Results */}
        {game.status === 'playing' && questionResults && (
          <div className="text-center animate-fadeIn" style={{ width: '100%', maxWidth: '800px' }}>
            <h2 style={{ marginBottom: '20px' }}>Resultaten</h2>
            
            <div 
              className="card mb-3" 
              style={{ 
                background: 'var(--color-success)', 
                padding: '20px',
                fontSize: '1.5rem',
                fontWeight: 600
              }}
            >
              Correct antwoord: {questionResults.correctAnswer}
            </div>
            
            {questionResults.sabotageUsed && (
              <div className="hint-box mb-3" style={{ background: 'var(--color-primary)' }}>
                🦊 De Mol heeft gesaboteerd: {questionResults.sabotageUsed.type}
              </div>
            )}
            
            <div className="scoreboard">
              {questionResults.teamResults
                .sort((a, b) => b.points - a.points)
                .map((result) => (
                  <div 
                    key={result.teamId}
                    className="scoreboard-item"
                    style={{ 
                      borderLeft: `4px solid ${result.correct ? 'var(--color-success)' : 'var(--color-error)'}` 
                    }}
                  >
                    <div className="scoreboard-team">
                      {result.correct ? '✅' : '❌'} {result.teamName}
                    </div>
                    <div className="scoreboard-score">
                      {result.answer || 'Geen antwoord'}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Waiting for next question */}
        {game.status === 'playing' && !activeQuestion && !questionResults && (
          <div className="text-center animate-fadeIn">
            <h2>Klaar voor de volgende vraag?</h2>
            <p className="text-muted mt-2">Wacht op de spelleider...</p>
          </div>
        )}

        {/* Voting */}
        {game.status === 'voting' && (
          <div className="text-center animate-fadeIn">
            <h1 style={{ fontSize: '4rem', color: 'var(--color-primary)', marginBottom: '20px' }}>
              🔍 WIE IS DE MOL?
            </h1>
            <p style={{ fontSize: '1.5rem', marginBottom: '40px' }}>
              Stem nu op jullie telefoon!
            </p>
            
            <div className="flex gap-2" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
              {game.teams.map(team => (
                <div 
                  key={team.id}
                  className="card"
                  style={{ 
                    padding: '16px 32px',
                    background: team.molVote ? 'var(--color-success)' : 'var(--color-secondary)'
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{team.name}</span>
                  <span style={{ marginLeft: '12px' }}>
                    {team.molVote ? '✅ Gestemd' : '⏳'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Final Results */}
        {game.status === 'finished' && finalResults && (
          <div className="text-center animate-fadeIn" style={{ width: '100%', maxWidth: '800px' }}>
            <h1 style={{ fontSize: '4rem', color: 'var(--color-accent)', marginBottom: '10px' }}>
              🏆 EINDSTAND
            </h1>
            
            <div 
              className="card mb-3" 
              style={{ 
                background: 'var(--color-primary)', 
                padding: '20px',
                fontSize: '1.5rem'
              }}
            >
              🦊 De Mol was: <strong>De Spelleider!</strong>
            </div>
            
            <div className="scoreboard">
              {finalResults.teams.map((team, index) => (
                <div 
                  key={team.teamId}
                  className={`scoreboard-item ${index === 0 ? 'winner' : ''}`}
                  style={{ fontSize: index === 0 ? '1.25rem' : '1rem' }}
                >
                  <div className="scoreboard-rank">#{index + 1}</div>
                  <div className="scoreboard-team">
                    {index === 0 && '👑 '}{team.teamName}
                  </div>
                  <div className="scoreboard-score">
                    <span>{team.totalScore} pts</span>
                    {team.molBonus > 0 && (
                      <span style={{ fontSize: '0.75rem', marginLeft: '8px', color: 'var(--color-accent)' }}>
                        (+{team.molBonus} Mol bonus!)
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer - Scores */}
      {game.status === 'playing' && (
        <div className="presentation-footer">
          <div className="flex gap-3" style={{ overflowX: 'auto' }}>
            {[...game.teams]
              .sort((a, b) => b.score - a.score)
              .map((team, index) => (
                <div key={team.id} className="flex gap-1" style={{ whiteSpace: 'nowrap' }}>
                  <span style={{ color: 'var(--color-text-muted)' }}>#{index + 1}</span>
                  <span style={{ fontWeight: 600 }}>{team.name}</span>
                  <span className="score-badge">{team.score}</span>
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}
